import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchWalletStats, fetchPortfolioValue } from '../services/cieloService';
import { getTaxAdvice } from '../services/openaiService';
import Navbar from './Navbar';

const StatCard = ({ title, value, subValue, icon }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-[#BFEA15]/20">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
        {subValue && (
          <p className={`text-sm ${subValue.startsWith('-') ? 'text-red-500' : 'text-green-500'}`}>
            {subValue}
          </p>
        )}
      </div>
      {icon && <span className="text-2xl">{icon}</span>}
    </div>
  </div>
);

const PreSaleBanner = () => {
  const address = "HG9t7k8GYkEWCZV4mMYVyGMJBwh3jeghipYawwnbwWHQ";
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(address);
      alert("Address copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy address:", err);
    }
  };

  

};

const Dashboard = () => {
  const { walletAddress } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [noData, setNoData] = useState(false);
  const [taxStats, setTaxStats] = useState(null);
  const [aiTaxAdvice, setAiTaxAdvice] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    document.title = "IRScan AI - Best Solana Tax Calculator";
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!walletAddress) return;
      
      setLoading(true);
      try {
        const [statsData, portfolioData] = await Promise.all([
          fetchWalletStats(walletAddress),
          fetchPortfolioValue(walletAddress)
        ]);

        // Calculate tax implications
        const taxImplications = {
          estimatedTax: Math.abs(statsData.realizedPnl * 0.3),
          shortTermGains: statsData.realizedPnl > 0 ? statsData.realizedPnl : 0,
          longTermGains: 0,
          totalTaxableGains: statsData.realizedPnl > 0 ? statsData.realizedPnl : 0,
          taxRate: '30%',
          taxYear: new Date().getFullYear()
        };

        if (statsData.combinedPnl === 0 && 
            statsData.realizedPnl === 0 && 
            statsData.unrealizedPnl === 0 && 
            statsData.tokensTraded === 0) {
          setNoData(true);
        }

        setStats(statsData);
        setPortfolio(portfolioData);
        setTaxStats(taxImplications);

        // Get AI tax advice if there are earnings
        if (statsData.combinedPnl !== 0) {
          setAiLoading(true);
          try {
            const advice = await getTaxAdvice(statsData.combinedPnl.toFixed(2));
            setAiTaxAdvice(advice);
          } catch (err) {
            console.error('Failed to get AI tax advice:', err);
          } finally {
            setAiLoading(false);
          }
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [walletAddress]);

  if (!walletAddress) {
    navigate('/');
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar walletAddress={walletAddress} />
      <div className="flex-1 overflow-auto pt-20">
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <PreSaleBanner />
            {loading ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#BFEA15] border-t-transparent"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500">{error}</div>
            ) : noData ? (
              <div className="text-center text-gray-500">No transactions found for this wallet.</div>
            ) : (
              <div className="space-y-8">
                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="Portfolio Value"
                    value={`$${Math.abs(portfolio?.current || 0).toFixed(2)}`}
                    subValue={portfolio?.changePercent24h ? `${portfolio.changePercent24h >= 0 ? '+' : ''}${portfolio.changePercent24h.toFixed(2)}% (24h)` : '+0.00% (24h)'}
                    icon="💼"
                  />
                  <StatCard
                    title="Total PNL"
                    value={`${stats?.combinedPnl >= 0 ? '+' : '-'}$${Math.abs(stats?.combinedPnl || 0).toFixed(2)}`}
                    subValue={stats?.winRate ? `Win Rate: ${Math.min(100, Math.max(0, stats.winRate)).toFixed(1)}%` : 'Win Rate: 0.0%'}
                    icon="📈"
                  />
                  <StatCard
                    title="Tokens Traded"
                    value={stats?.tokensTraded || 0}
                    subValue={stats?.averageHoldingTime ? `Avg Hold: ${Math.round(stats.averageHoldingTime)} days` : 'Avg Hold: 0 days'}
                    icon="🔄"
                  />
                  <StatCard
                    title="Estimated Tax"
                    value={stats?.combinedPnl >= 0 ? `$${taxStats?.estimatedTax.toFixed(2)}` : '$0.00'}
                    subValue={`Rate: ${taxStats?.taxRate || '0%'}`}
                    icon="📊"
                  />
                </div>

                {/* PNL and Tax Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-[#BFEA15]/20"
                  >
                    <h2 className="text-xl font-bold text-gray-800 mb-4">PNL Breakdown</h2>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Realized PNL</span>
                        <span className={`font-medium ${stats?.realizedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {stats?.realizedPnl >= 0 ? '+' : '-'}${Math.abs(stats?.realizedPnl || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Unrealized PNL</span>
                        <span className={`font-medium ${stats?.unrealizedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {stats?.unrealizedPnl >= 0 ? '+' : '-'}${Math.abs(stats?.unrealizedPnl || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Combined PNL</span>
                          <span className={`font-medium ${stats?.combinedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {stats?.combinedPnl >= 0 ? '+' : '-'}${Math.abs(stats?.combinedPnl || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-[#BFEA15]/20"
                  >
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Tax Analysis</h2>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Short-term Gains</span>
                        <span className="font-medium text-gray-800">
                          ${Math.max(0, taxStats?.shortTermGains || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Long-term Gains</span>
                        <span className="font-medium text-gray-800">
                          ${Math.max(0, taxStats?.longTermGains || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Estimated Tax ({taxStats?.taxRate})</span>
                          <span className="font-medium text-gray-800">
                            ${stats?.combinedPnl >= 0 ? (taxStats?.estimatedTax || 0).toFixed(2) : '0.00'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* AI Tax Analysis */}
                {aiTaxAdvice && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-[#BFEA15]/20"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <h2 className="text-xl font-bold text-gray-800">AI Tax Analysis</h2>
                        <span className="text-xl">🤖</span>
                      </div>
                      <button
                        onClick={() => setAiTaxAdvice(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="prose prose-sm max-w-none max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-4">
                      <p className="text-gray-600 whitespace-pre-line">{aiTaxAdvice}</p>
                    </div>
                    <div className="mt-4 text-xs text-gray-500 border-t pt-4">
                      Note: This is an AI-generated analysis. Please consult with a tax professional for accurate advice.
                    </div>
                  </motion.div>
                )}

                {/* Portfolio Breakdown */}
                {portfolio?.tokens && portfolio.tokens.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-[#BFEA15]/20"
                  >
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Portfolio Breakdown</h2>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-3 text-gray-500 font-medium">Token</th>
                            <th className="text-right py-3 text-gray-500 font-medium">Amount</th>
                            <th className="text-right py-3 text-gray-500 font-medium">Value</th>
                            <th className="text-right py-3 text-gray-500 font-medium">24h Change</th>
                          </tr>
                        </thead>
                        <tbody>
                          {portfolio.tokens.map((token, index) => (
                            <tr key={index} className="border-b border-gray-50">
                              <td className="py-3">
                                <div className="font-medium text-gray-800">{token.symbol}</div>
                              </td>
                              <td className="text-right text-gray-600">
                                {token.amount.toFixed(4)}
                              </td>
                              <td className="text-right font-medium text-gray-800">
                                ${token.value.toFixed(2)}
                              </td>
                              <td className={`text-right ${token.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {token.change24h >= 0 ? '+' : ''}{token.change24h.toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
